import { ReactNode } from 'react';
import { Box, Paper, Typography, styled } from '@mui/material';
import { alpha } from '@mui/material/styles';

interface IconToggleOption {
  value: string;
  icon: ReactNode;
  label: string;
}

interface IconToggleButtonGroupProps {
  options: IconToggleOption[];
  value: string;
  onChange: (value: string) => void;
}

const StyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  fontFamily: 'Source Sans Pro',
  fontSize: '14px',
  fontStyle: 'normal',
  lineHeight: 'normal',
  ...(isSelected
    ? {
        color: theme.palette.common.white,
        fontWeight: 600,
      }
    : {
        color: theme.palette.text.primary,
        fontWeight: 400,
      }),
}));

const IconWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  '& .MuiSvgIcon-root': {
    width: '24px',
    height: '24px',
    color: isSelected ? theme.palette.common.white : theme.palette.text.primary,
  },
}));

const ToggleItem = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isFirst' && prop !== 'isLast',
})<{ isSelected?: boolean; isFirst?: boolean; isLast?: boolean }>(({ theme, isSelected, isFirst, isLast }) => ({
  width: '165px',
  padding: '20px 0',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.5),
  cursor: 'pointer',
  transition: theme.transitions.create(['background-color', 'box-shadow']),
  backgroundColor: isSelected
    ? theme.palette.brand.main
    : theme.palette.mode === 'dark'
      ? theme.palette.background.grey
      : theme.palette.common.white,
  border: `1px solid ${alpha(theme.palette.text.primary, 0.23)}`,
  borderRadius: 0,
  marginLeft: -1,
  boxShadow: isSelected ? 'inset 2px 3px 3px 0 rgba(0, 0, 0, 0.10)' : 'none',
  '&:hover': {
    zIndex: 1,
  },

  // Desktop styles
  ...(isFirst && {
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
  }),

  ...(isLast && {
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
  }),

  // Mobile styles
  [theme.breakpoints.down('md')]: {
    width: '100%',
    minWidth: '250px',
    border: `1px solid ${alpha(theme.palette.text.primary, 0.23)}`,
    marginLeft: 0,
    marginTop: -1,
    '&:last-of-type': {
      marginBottom: 0,
    },
    borderRadius: 0,
    ...(isFirst && {
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
    }),
    ...(isLast && {
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8,
    }),
  },
}));

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',

  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'center',
  },
}));

export const IconToggleButtonGroup = ({ options, value, onChange }: IconToggleButtonGroupProps) => {
  return (
    <Container role="radiogroup">
      {options.map((option, index) => (
        <ToggleItem
          key={option.value}
          elevation={0}
          isSelected={value === option.value}
          isFirst={index === 0}
          isLast={index === options.length - 1}
          onClick={() => onChange(option.value === value ? '' : option.value)}
          role="radio"
          aria-checked={value === option.value}
        >
          <IconWrapper isSelected={value === option.value}>{option.icon}</IconWrapper>
          <StyledTypography isSelected={value === option.value}>{option.label}</StyledTypography>
        </ToggleItem>
      ))}
    </Container>
  );
};
